import React, { useEffect, useMemo, useState } from 'react';
import { Hash, Loader2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Avatar, AvatarFallback } from '@easyflow/ui-frontend/components/ui/avatar';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { ARTIFACT_EXPORT_FORMATS, ARTIFACT_EXPORT_FORMAT_LABELS } from '@easyflow/shared';
import type { ArtifactExportFormat, ShareArtifactRequest, ShareArtifactResponse } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { messengerApi } from '@/pages/messenger/lib/messengerApi';
import type { ConversationSummary, ParticipantBrief } from '@/pages/messenger/types';
import type { WorkspaceUiArtifact } from './types';

// Task #3846 — condivisione artefatto via Messenger: picker colleghi +
// gruppi/canali del proprio tenant. L'artefatto arriva come allegato standard
// (snapshot durevole), la conversazione PA d'origine resta invisibile.
interface Props {
  artifact: WorkspaceUiArtifact;
  open: boolean;
  onClose: () => void;
}

function userDisplayName(u: ParticipantBrief): string {
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Utente';
}

function initialsOf(name: string): string {
  return name.split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('') || '?';
}

export const ShareArtifactDialog: React.FC<Props> = ({ artifact, open, onClose }) => {
  const { toast } = useToast();
  const formats = useMemo<readonly ArtifactExportFormat[]>(
    () => ARTIFACT_EXPORT_FORMATS[artifact.kind] ?? [],
    [artifact.kind],
  );
  const [format, setFormat] = useState<ArtifactExportFormat | null>(formats[0] ?? null);
  const [search, setSearch] = useState('');
  const [note, setNote] = useState('');
  const [users, setUsers] = useState<ParticipantBrief[]>([]);
  const [groups, setGroups] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFormat(formats[0] ?? null);
    setSearch('');
    setNote('');
    setSendingKey(null);
  }, [open, formats]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      Promise.all([
        messengerApi.searchUsers(search),
        messengerApi.listConversations({ search: search || undefined }),
      ])
        .then(([usersRes, convsRes]) => {
          if (cancelled) return;
          setUsers(usersRes.data ?? []);
          setGroups((convsRes.data ?? []).filter(c => c.type !== 'direct'));
        })
        .catch(() => {
          if (cancelled) return;
          toast({ title: 'Errore', description: 'Caricamento di colleghi e gruppi fallito', variant: 'destructive' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [open, search, toast]);

  const handleShare = async (target: ShareArtifactRequest['target'], key: string, label: string) => {
    if (!format || sendingKey) return;
    setSendingKey(key);
    try {
      const body: ShareArtifactRequest = {
        format,
        target,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const res = await apiClient.post<ShareArtifactResponse>(
        `/personal-assistant/workspace/artifacts/${artifact.id}/share`,
        body,
      );
      if (!res.success) throw new Error(res.error || 'Condivisione fallita');
      toast({ title: 'Artefatto condiviso', description: `"${artifact.title}" inviato a ${label}` });
      onClose();
    } catch (err) {
      toast({
        title: 'Condivisione fallita',
        description: (err as Error).message || 'Errore imprevisto durante la condivisione',
        variant: 'destructive',
      });
    } finally {
      setSendingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Condividi «{artifact.title}»</DialogTitle>
        </DialogHeader>

        {formats.length > 1 && format && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Formato:</span>
            {formats.map(f => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  f === format
                    ? 'border-violet-400 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
              >
                {ARTIFACT_EXPORT_FORMAT_LABELS[f]}
              </button>
            ))}
          </div>
        )}

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca colleghi, gruppi o canali…"
          className="h-9"
        />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Messaggio (facoltativo)"
          maxLength={2000}
          className="h-9"
        />

        <ScrollArea className="h-72">
          {loading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {!loading && users.length === 0 && groups.length === 0 && (
            <p className="py-8 text-center text-xs text-muted-foreground">Nessun collega o gruppo trovato</p>
          )}

          {!loading && users.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 px-1 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Users className="h-3 w-3" /> Colleghi
              </div>
              {users.map(u => {
                const name = userDisplayName(u);
                const key = `user:${u.id}`;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={sendingKey !== null}
                    onClick={() => void handleShare({ kind: 'user', userId: u.id }, key, name)}
                    className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-indigo-500 text-xs font-semibold text-white">
                        {initialsOf(name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
                    {sendingKey === key && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
                  </button>
                );
              })}
            </>
          )}

          {!loading && groups.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 px-1 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Hash className="h-3 w-3" /> Gruppi e canali
              </div>
              {groups.map(c => {
                const label = c.display_title || c.title || 'Conversazione';
                const key = `conv:${c.id}`;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={sendingKey !== null}
                    onClick={() => void handleShare({ kind: 'conversation', conversationId: c.id }, key, label)}
                    className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-gradient-to-br from-slate-500 to-slate-700 text-xs font-semibold text-white">
                        {c.type === 'channel' ? '#' : initialsOf(label)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
                    {sendingKey === key && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
                  </button>
                );
              })}
            </>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
